import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import AuthForm from "./AuthForm";
import analytics, { initializeAnalytics } from '../services/analytics';
import api from '../services/api';
import LaunchCalendar from './LaunchCalendar';
import LaunchModal from './LaunchModal';
import { RootState } from '../redux/store';
import './HeroStyles.css';
import './ImprovedModal.css';
import './CosmicButton.css';

interface SpaceXLaunch {
  name: string;
  date_utc: string;
  date_unix: number;
  details: string;
  rocket_name: string;
  launchpad_name: string;
  launchpad_full_name: string;
  webcast_live: boolean;
  youtube_id: string;
  patch: string;
}

interface Launch {
  id: string;
  name: string;
  provider: string;
  launchTime: string;
  launchSite: string;
  missionType: string;
  status: 'GO' | 'HOLD' | 'TBD' | 'SCRUB';
  providerLogo?: string;
  launchPad?: string;
  description?: string; // Made optional to match LaunchCalendar's output
  streamUrl?: string;
  weatherGo?: number;
  vehicleType?: string;
  missionPatch?: string;
  payload?: string;
  orbit?: string;
}

interface HeroProps {
  onCalendarOpen?: () => void;
  selectedLaunch?: Launch | null;
  onSelectLaunch?: (launch: Launch) => void;
  onCloseModal?: () => void;
}

const Hero: React.FC<HeroProps> = ({ 
  onCalendarOpen, 
  selectedLaunch: externalSelectedLaunch,
  onSelectLaunch: _externalOnSelectLaunch,
  onCloseModal 
}) => {
  // Get authentication state from Redux
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const [launch, setLaunch] = useState<SpaceXLaunch | null>(null);
  const [, setCountdown] = useState({
    days: 12,
    hours: 18,
    minutes: 45,
    seconds: 33,
  });
  const [cardAnimated, setCardAnimated] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedLaunch, setSelectedLaunch] = useState<Launch | null>(null);
  const starfieldCanvasRef = useRef<HTMLCanvasElement>(null);
  const earthLightsRef = useRef<HTMLDivElement>(null);
  const [, setIsLoading] = useState(true);
  const [, setError] = useState<string | null>(null);
  const [, setScrolled] = useState(false);
  const [showLaunchModal, setShowLaunchModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [upcomingLaunch, setUpcomingLaunch] = useState<Launch | null>(null);
  const [launchCountdown, setLaunchCountdown] = useState<{days:number,hours:number,minutes:number,seconds:number}>({days:0,hours:0,minutes:0,seconds:0});
  const [launchLoading, setLaunchLoading] = useState(true);
  const [, setLaunchError] = useState<string|null>(null);
  const [usingDemoLaunch, setUsingDemoLaunch] = useState(false);

  // Demo fallback data
  const demoLaunch: Launch = useMemo(() => ({
    id: 'demo-1',
    name: 'Falcon 9 Block 5 | Starlink Group 6-23',
    provider: 'SpaceX',
    launchTime: new Date(Date.now() + 86400000 * 2).toISOString(),
    launchSite: 'Kennedy Space Center',
    missionType: 'Communications',
    status: 'GO',
    description: 'Demo launch of Starlink satellites',
    launchPad: 'LC-39A',
    providerLogo: '',
    streamUrl: '',
    weatherGo: 90,
    vehicleType: 'Falcon 9',
    missionPatch: '',
    payload: 'Starlink satellites',
    orbit: 'LEO'
  }), []);

  // Initialize analytics
  useEffect(() => {
    initializeAnalytics();
  }, []);

  // Fetch SpaceX launch data
  useEffect(() => {
    const fetchLaunchData = async () => {
      try {
        setIsLoading(true);
        
        // Try multiple endpoints for launch data
        let response;
        try {
          response = await api.get('/api/spacex/next-launch');
        } catch (firstError: any) {
          // Try alternative endpoint if first fails
          try {
            response = await api.get('/api/launches/next');
          } catch (secondError: any) {
            throw firstError; // Use original error
          }
        }
        
        setLaunch(response.data);
        setError(null);
        
        // Track successful launch data fetch
        analytics.trackEvent('launch_data_fetched', { launchName: response.data.name });
      } catch (err: any) {
        console.debug('Launch data unavailable, using fallback:', err.message);
        
        // Use fallback data if API fails
        if (err.response?.status === 404 || err.message?.includes('404')) {
          // API endpoint not available, use fallback data
          const fallbackLaunch = {
            name: 'Next SpaceX Launch',
            date_utc: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
            date_unix: Math.floor((Date.now() + 7 * 24 * 60 * 60 * 1000) / 1000),
            details: 'Upcoming SpaceX mission launching soon',
            rocket_name: 'Falcon 9',
            launchpad_name: 'Kennedy Space Center',
            launchpad_full_name: 'Kennedy Space Center Launch Complex 39A',
            webcast_live: false,
            youtube_id: '',
            patch: ''
          };
          setLaunch(fallbackLaunch);
          setError(null); // Don't show error for missing endpoint
        } else {
          setError('Failed to fetch launch data');
        }
        
        // Track error
        const errorMsg = err instanceof Error ? err.message : String(err);
        analytics.trackEvent('launch_data_error', { error: errorMsg });
      } finally {
        setIsLoading(false);
      }
    };

    fetchLaunchData();
    const interval = setInterval(fetchLaunchData, 5 * 60 * 1000); // Refresh every 5 minutes
    return () => clearInterval(interval);
  }, []);

  // Handle countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      if (launch?.date_unix) {
        const now = Math.floor(Date.now() / 1000);
        const timeLeft = launch.date_unix - now;

        if (timeLeft <= 0) {
          clearInterval(timer);
          setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
          return;
        }

        const days = Math.floor(timeLeft / (24 * 60 * 60));
        const hours = Math.floor((timeLeft % (24 * 60 * 60)) / (60 * 60));
        const minutes = Math.floor((timeLeft % (60 * 60)) / 60);
        const seconds = Math.floor(timeLeft % 60);

        setCountdown({ days, hours, minutes, seconds });
      } else {
        // Static countdown when no API data
        setCountdown((prev) => {
          let { days, hours, minutes, seconds } = prev;
          seconds--;
          if (seconds < 0) {
            seconds = 59;
            minutes--;
            if (minutes < 0) {
              minutes = 59;
              hours--;
              if (hours < 0) {
                hours = 23;
                days--;
              }
            }
          }
          return { days, hours, minutes, seconds };
        });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [launch]);

  // Handle scroll state
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 30) {
        setScrolled(true);
        setCardAnimated(true);
      } else {
        setScrolled(false);
        setCardAnimated(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const canvas = starfieldCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    function resizeCanvas() {
      if (!canvas) return;
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      drawStars();
    }

    function drawStars() {
      if (!canvas || !ctx) return;
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const starCount = Math.floor((canvas.width * canvas.height) / 2500);
      for (let i = 0; i < starCount; i++) {
        const x = Math.random() * canvas.width;
        const y = Math.random() * canvas.height;
        const radius = Math.random() * 0.7 + 0.6;
        const opacity = Math.random() * 0.5 + 0.2;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = `rgba(255,255,255,${opacity})`;
        ctx.fill();
      }
    }

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);
    return () => window.removeEventListener('resize', resizeCanvas);
  }, []);
  
  // Effect to manage Earth's day/night cycle and city lights
  useEffect(() => {
    if (!earthLightsRef.current) return;
    
    // Get the current hour to determine if it's day or night
    const currentHour = new Date().getHours();
    const isDarkHours = currentHour < 6 || currentHour > 18; // Night time between 6 PM and 6 AM
    
    // Show city lights during night hours with a smooth transition
    if (isDarkHours) {
      earthLightsRef.current.style.display = 'block';
      earthLightsRef.current.style.opacity = '0';
      
      // Fade in the lights
      setTimeout(() => {
        if (earthLightsRef.current) {
          earthLightsRef.current.style.transition = 'opacity 2s ease-in';
          earthLightsRef.current.style.opacity = '0.6';
        }
      }, 1000);
    } else {
      // It's daytime, hide the lights
      if (earthLightsRef.current) {
        earthLightsRef.current.style.display = 'none';
      }
    }
    
    // Interval to rotate the day/night effect based on time
    const interval = setInterval(() => {
      const date = new Date();
      const hour = date.getHours();
      const minute = date.getMinutes();
      
      // Calculate rotation based on time (24 hours = 360 degrees)
      const rotationDegree = ((hour * 60 + minute) / (24 * 60)) * 360;
      
      // Apply rotation to the night overlay to simulate Earth's rotation
      const nightElement = document.querySelector('.hero-earth-night') as HTMLElement;
      if (nightElement) {
        nightElement.style.transform = `rotate(${rotationDegree}deg)`;
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Fetch the next real launch from backend aggregator, with fallback and polling
  useEffect(() => {
    let isMounted = true;
    let pollInterval: NodeJS.Timeout;
    const fetchNextLaunch = async () => {
      setLaunchLoading(true);
      setLaunchError(null);
      try {
        const now = new Date();
        const end = new Date(now.getTime() + 1000*60*60*24*30); // 30 days ahead
        const response = await fetch(`https://spacepings.space/api/launches/upcoming?startDate=${now.toISOString()}&endDate=${end.toISOString()}&limit=1`);
        if (!response.ok) throw new Error('Failed to fetch launch data');
        const launches = await response.json();
        if (isMounted && launches && launches.length > 0) {
          const launch = launches[0];
          setUpcomingLaunch({
            id: launch.id || String(Date.now()),
            name: launch.name || 'Unknown Mission',
            provider: launch.provider || 'Unknown',
            launchTime: launch.launchTime || new Date().toISOString(),
            launchSite: launch.launchSite || 'Unknown',
            missionType: launch.missionType || 'Unknown',
            status: launch.status || 'TBD',
            description: launch.description || '',
            launchPad: launch.launchPad || '',
            providerLogo: launch.providerLogo || '',
            streamUrl: launch.streamUrl || '',
            weatherGo: launch.weatherGo || undefined,
            vehicleType: launch.vehicleType || '',
            missionPatch: launch.missionPatch || '',
            payload: launch.payload || '',
            orbit: launch.orbit || ''
          });
          setUsingDemoLaunch(false);
        } else if (isMounted) {
          setUpcomingLaunch(demoLaunch);
          setUsingDemoLaunch(true);
        }
      } catch (e:any) {
        if (isMounted) {
          setLaunchError(e.message || 'Could not load launch data');
          setUpcomingLaunch(demoLaunch);
          setUsingDemoLaunch(true);
        }
      } finally {
        if (isMounted) setLaunchLoading(false);
      }
    };
    fetchNextLaunch();
    pollInterval = setInterval(fetchNextLaunch, 60000); // poll every 60s
    return () => { isMounted = false; clearInterval(pollInterval); };
  }, [demoLaunch]);

  // Live countdown for the next launch
  useEffect(() => {
    if (!upcomingLaunch) return;
    const interval = setInterval(() => {
      const launchDate = new Date(upcomingLaunch.launchTime).getTime();
      const now = Date.now();
      let diff = Math.max(0, launchDate - now);
      const days = Math.floor(diff / (1000*60*60*24));
      diff -= days * 1000*60*60*24;
      const hours = Math.floor(diff / (1000*60*60));
      diff -= hours * 1000*60*60;
      const minutes = Math.floor(diff / (1000*60));
      diff -= minutes * 1000*60;
      const seconds = Math.floor(diff / 1000);
      setLaunchCountdown({ days, hours, minutes, seconds });
    }, 1000);
    return () => clearInterval(interval);
  }, [upcomingLaunch]);

  return (
    <section className="hero-section">
      {/* Starfield Canvas Background */}
      <canvas ref={starfieldCanvasRef} className="starfield-bg" tabIndex={-1} aria-hidden="true" />
      {/* Cosmic Sky Background */}
      <div className="cosmic-sky"></div>
      <div className="cosmic-stars"></div>
      <div className="shooting-star-static"></div>
      <div className="shooting-star-static"></div>
      <div className="shooting-star-static"></div>

      {/* Cosmic nebula effect */}
      <div className="cosmic-nebula"></div>
      <div className="cosmic-dust"></div>

      {/* Main hero content */}
      <div className="hero-container">
        {/* Main Hero Area: Text and Earth Animation */}
        <div className="main-hero-area">
          <div className="hero-content">
            <h1 className="hero-title">
              Track Satellites in Real-Time
            </h1>
            <h2 className="hero-subtitle-large">
              Get <span className="highlight pulse-text">PINGED</span> When
              Satellites Are
              <span className="highlight glow-text"> Visible</span> Above You
            </h2>

            <p className="hero-subtitle">
              Real-time alerts for Starlink, ISS, and space debris passes.
              <span className="emphasis"> Only when skies are clear.</span>
            </p>
          </div>

          <div className="hero-earth-container">
            <div className="hero-earth-visualization">
              <div className="hero-earth-glow"></div>
              <div className="hero-earth-atmosphere"></div>
              <div className="hero-earth-sphere">
                <div className="hero-earth-surface"></div>
                <div className="hero-earth-clouds"></div>
                <div className="hero-earth-night"></div>
                <div className="hero-earth-lights" ref={earthLightsRef}></div>
                <div className="hero-earth-reflection"></div>
              </div>

              <div className="hero-orbit-system">
                <div className="hero-orbit hero-orbit-1"></div>
                <div className="hero-orbit hero-orbit-2"></div>
                <div className="hero-orbit hero-orbit-3"></div>

                {/* ISS */}
                <div className="hero-spacecraft hero-iss">
                  <div className="spacecraft-body">ISS</div>
                  <div className="spacecraft-solar-panels"></div>
                </div>

                {/* Satellite */}
                <div className="hero-spacecraft hero-satellite">
                  <div className="spacecraft-body"></div>
                  <div className="spacecraft-dish"></div>
                </div>

                {/* SpaceX Dragon */}
                <div className="hero-spacecraft hero-dragon">
                  <div className="spacecraft-body">Dragon</div>
                  <div className="spacecraft-capsule"></div>
                </div>

                {/* Asteroid 1 */}
                <div className="hero-asteroid hero-asteroid-1">
                  <div className="asteroid-body"></div>
                </div>

                {/* Asteroid 2 */}
                <div className="hero-asteroid hero-asteroid-2">
                  <div className="asteroid-body"></div>
                </div>

                {/* Starlink constellation */}
                <div className="hero-starlink-constellation">
                  {Array.from({ length: 8 }).map((_, index) => (
                    <div
                      key={index}
                      className="starlink-satellite"
                      style={{ 
                        animationDelay: `${index * 0.5}s`,
                        transform: `rotate(${index * 45}deg)`
                      }}
                    >
                      <div className="starlink-dot"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Secondary Content Area: Signup and Rocket Launch Watch Area */}
        <div className="night-sky-section" style={{ position: 'relative', width: '100%', zIndex: 1, marginTop: 0, marginBottom: 0, padding: '64px 0 0 0' }}>
          {/* Night Sky Background Layers */}
          <div className="night-sky" style={{ pointerEvents: 'none' }}>
            <div className="stars-layer-1" style={{ pointerEvents: 'none' }}></div>
            <div className="stars-layer-2" style={{ pointerEvents: 'none' }}></div>
            <div className="stars-layer-3" style={{ pointerEvents: 'none' }}></div>
            <div className="nebula-layer" style={{ pointerEvents: 'none' }}></div>
            <div className="rocket rocket-1" style={{ pointerEvents: 'none' }}></div>
            <div className="rocket rocket-2" style={{ pointerEvents: 'none' }}></div>
          </div>
          <div className="secondary-hero-area" style={{ display: 'flex', flexDirection: 'row', gap: '32px', alignItems: 'flex-start', justifyContent: 'center', position: 'relative', zIndex: 3, minHeight: '480px', isolation: 'isolate' }}>
            {/* Auth Form - Only show if user is not authenticated */}
            {!isAuthenticated && (
              <div className={`auth-box-container ${cardAnimated ? "slide-active" : ""}`} style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
                <div className="auth-box cosmic-glow-box" style={{ position: 'relative', zIndex: 2 }}>
                  <AuthForm />
                  <div className="auth-login-link" style={{ textAlign: 'center', marginTop: '10px', padding: '5px' }}>
                    <span style={{ color: '#a0c0ff', fontSize: '0.9rem' }}>Already have an account? </span>
                    <a href="/auth" style={{ 
                      color: '#7b3fe4', 
                      textDecoration: 'none',
                      fontWeight: 'bold',
                      transition: 'color 0.3s ease'
                    }}>
                      Sign In
                    </a>
                  </div>
                </div>
              </div>
            )}
            
            {/* Authenticated User Welcome - Show if user is authenticated */}
            {isAuthenticated && (
              <div className={`auth-box-container ${cardAnimated ? "slide-active" : ""}`} style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', position: 'relative', zIndex: 2 }}>
                <div className="auth-box cosmic-glow-box" style={{ position: 'relative', zIndex: 2, padding: '20px', textAlign: 'center' }}>
                  <div style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '2rem', marginBottom: '8px' }}>🚀</div>
                    <h3 style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 600, marginBottom: '8px' }}>Welcome Back!</h3>
                    <p style={{ color: '#a0c0ff', fontSize: '0.9rem', marginBottom: '16px' }}>Ready to track satellites and launches?</p>
                  </div>
                  <a href="/dashboard" className="cosmic-button primary-button" style={{ width: '100%', padding: '12px 16px', fontSize: '0.9rem' }}>
                    <span>🎯 Go to Dashboard</span>
                  </a>
                </div>
              </div>
            )}
            
            {/* Next Launch Preview */}
            <div className="next-launch-preview auth-box cosmic-glow-box" style={{ flex: 1, minWidth: 220, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start', padding: '20px 18px', background: 'rgba(18,19,26,0.97)', borderRadius: '18px', boxShadow: '0 4px 32px rgba(102,126,234,0.10)', border: '1.5px solid #23243a', height: 'fit-content', position: 'relative', zIndex: 2, alignSelf: 'center', marginTop: '80px', overflow: 'hidden' }}>
              {/* Floating satellites background */}
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, opacity: 0.1, pointerEvents: 'none' }}>
                {Array.from({ length: 6 }).map((_, i) => (
                  <div
                    key={i}
                    style={{
                      position: 'absolute',
                      width: '8px',
                      height: '8px',
                      background: '#667eea',
                      borderRadius: '50%',
                      top: `${Math.random() * 80 + 10}%`,
                      left: `${Math.random() * 80 + 10}%`,
                      animation: `float ${3 + i * 0.5}s ease-in-out infinite`,
                      animationDelay: `${i * 0.8}s`
                    }}
                  />
                ))}
              </div>

              {/* Header with informative title */}
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 18, position: 'relative', zIndex: 1 }}>
                <span style={{ fontSize: 28, marginRight: 10, filter: 'drop-shadow(0 0 6px #667eea)' }}>🚀</span>
                <h2 style={{ fontWeight: 800, fontSize: '1.6rem', letterSpacing: '-0.01em', color: '#fff', margin: 0, textAlign: 'center' }}>
                  Upcoming Satellite Launches
                </h2>
              </div>
              
              <div style={{ color: '#8b92a9', fontSize: '1rem', marginBottom: 20, textAlign: 'center', position: 'relative', zIndex: 1 }}>
                Track the next rocket launches in real-time
              </div>

              {/* Next Launch Preview Data */}
              {launchLoading ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px', position: 'relative', zIndex: 1 }}>
                  <div style={{ width: '40px', height: '40px', border: '3px solid rgba(102, 126, 234, 0.3)', borderTop: '3px solid #667eea', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '12px' }}></div>
                  <div style={{ color: '#8b92a9', fontSize: '0.9rem' }}>Loading next launch...</div>
                </div>
              ) : upcomingLaunch ? (
                <div style={{ width: '100%', position: 'relative', zIndex: 1 }}>
                  {/* Launch Info Card */}
                  <div style={{ padding: '16px', background: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px', border: '1px solid rgba(59, 130, 246, 0.2)', marginBottom: '16px' }}>
                    {/* Launch Name */}
                    <div style={{ fontSize: '1.1rem', fontWeight: 600, color: '#fff', marginBottom: '8px', lineHeight: '1.3' }}>
                      {upcomingLaunch.name}
                    </div>
                    
                    {/* Provider and Status */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                      <span style={{ fontSize: '0.85rem', color: '#3b82f6', fontWeight: 500 }}>
                        {upcomingLaunch.provider}
                      </span>
                      <span style={{ fontSize: '0.75rem', padding: '2px 8px', background: upcomingLaunch.status === 'GO' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(251, 191, 36, 0.2)', color: upcomingLaunch.status === 'GO' ? '#22c55e' : '#fbbf24', borderRadius: '4px', fontWeight: 500 }}>
                        {upcomingLaunch.status}
                      </span>
                      {usingDemoLaunch && (
                        <span style={{ fontSize: '0.7rem', padding: '2px 6px', background: 'rgba(139, 92, 246, 0.2)', color: '#8b5cf6', borderRadius: '4px', fontWeight: 500 }}>
                          DEMO
                        </span>
                      )}
                    </div>
                    
                    {/* Launch Site */}
                    <div style={{ fontSize: '0.85rem', color: '#a5b4fc', marginBottom: '8px' }}>
                      📍 {upcomingLaunch.launchSite}
                    </div>
                    
                    {/* Countdown */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', padding: '12px 0' }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fff' }}>{launchCountdown.days}</div>
                        <div style={{ fontSize: '0.7rem', color: '#8b92a9', textTransform: 'uppercase' }}>Days</div>
                      </div>
                      <div style={{ color: '#667eea', fontSize: '1.2rem', fontWeight: 700 }}>:</div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fff' }}>{String(launchCountdown.hours).padStart(2, '0')}</div>
                        <div style={{ fontSize: '0.7rem', color: '#8b92a9', textTransform: 'uppercase' }}>Hours</div>
                      </div>
                      <div style={{ color: '#667eea', fontSize: '1.2rem', fontWeight: 700 }}>:</div>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: '1.2rem', fontWeight: 700, color: '#fff' }}>{String(launchCountdown.minutes).padStart(2, '0')}</div>
                        <div style={{ fontSize: '0.7rem', color: '#8b92a9', textTransform: 'uppercase' }}>Mins</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Action buttons */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%' }}>
                    <button
                      onClick={() => setShowLaunchModal(true)}
                      className="cosmic-button primary-button"
                      style={{ fontSize: '0.9rem', padding: '10px 16px', width: '100%' }}
                    >
                      <span>🔍 View Full Details</span>
                    </button>
                    <button
                      onClick={() => setShowCalendarModal(true)}
                      className="cosmic-button secondary-button"
                      style={{ fontSize: '0.9rem', padding: '10px 16px', width: '100%', background: 'rgba(139, 92, 246, 0.1)', border: '1px solid rgba(139, 92, 246, 0.3)' }}
                    >
                      <span>📅 Launch Calendar</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', color: '#8b92a9', position: 'relative', zIndex: 1 }}>
                  <div style={{ fontSize: '2rem', marginBottom: '12px' }}>🛸</div>
                  <div style={{ marginBottom: '16px' }}>No upcoming launches found</div>
                  <button
                    onClick={() => setShowCalendarModal(true)}
                    className="cosmic-button primary-button"
                    style={{ fontSize: '0.9rem', padding: '10px 16px' }}
                  >
                    <span>Browse Calendar</span>
                  </button>
                </div>
              )}

              {/* Add CSS animation for floating effect */}
              <style>{`
                @keyframes float {
                  0%, 100% { transform: translateY(0px) translateX(0px); }
                  25% { transform: translateY(-3px) translateX(2px); }
                  50% { transform: translateY(-6px) translateX(-1px); }
                  75% { transform: translateY(-2px) translateX(1px); }
                }
                @keyframes pulse {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.5; }
                }
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>
          </div>
        </div>
      </div> {/* Closing hero-container */}
      
      {/* 
        Global Overlay Modals that float above the entire page
        Instead of embedding modals in the side-by-side view, we now show simplified
        summary information with buttons that trigger these full-screen overlay modals.
        This approach gives more space for content and avoids the sticky header issues.
      */}
      {showLaunchModal && upcomingLaunch && (
        <LaunchModal
          launch={upcomingLaunch as Launch}
          onClose={() => setShowLaunchModal(false)}
        />
      )}
      
      {showCalendarModal && (
        <div className="improved-modal-overlay" onClick={() => setShowCalendarModal(false)}>
          <div className="improved-modal-content cosmic-modal-content" onClick={e => e.stopPropagation()}>
            <div className="improved-modal-header cosmic-header">
              <h3>Cosmic Launch Calendar</h3>
              <button 
                className="improved-modal-close" 
                onClick={() => setShowCalendarModal(false)}
                aria-label="Close calendar"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="improved-modal-body cosmic-modal-body">
              <LaunchCalendar
                onSelectLaunch={launch => {
                  // When a launch is selected from the calendar, update the state and show its details
                  setUpcomingLaunch(launch);
                  setShowLaunchModal(true);
                  setShowCalendarModal(false);
                }}
                onClose={() => setShowCalendarModal(false)}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Launch Calendar Modal - only show internal calendar if no external handler */}
      {showCalendar && !onCalendarOpen && (
        <div className="improved-modal-overlay" onClick={() => setShowCalendar(false)}>
          <div className="improved-modal-content calendar-content side-by-side-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="improved-modal-header">
              <h3>Launch Calendar</h3>
              <button 
                className="improved-modal-close" 
                onClick={() => setShowCalendar(false)}
                aria-label="Close calendar"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="improved-modal-body">
              <LaunchCalendar 
                onSelectLaunch={(launch) => {
                  setSelectedLaunch(launch);
                  setShowCalendar(false);
                }}
                onClose={() => setShowCalendar(false)}
              />
            </div>
          </div>
        </div>
      )}
      
      {/* Launch Modal - Render as overlay popup */}
      {externalSelectedLaunch && (
        <LaunchModal
          launch={externalSelectedLaunch as Launch}
          onClose={onCloseModal!}
        />
      )}
      {selectedLaunch && !externalSelectedLaunch && (
        <LaunchModal
          launch={selectedLaunch as Launch}
          onClose={() => setSelectedLaunch(null)}
        />
      )}
    </section>
  );
};

export default Hero;